import {
  type GitChangeRequestState,
  type GitCiStatus,
  type GitLinkedTarget,
  type GitReviewState,
  type GitTimeMeta,
} from '@apm/contracts';
import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { displayDateTime } from '../../../lib/date';
import { cn } from '../../../lib/utils';
import {
  CHANGE_REQUEST_BADGE_CLASS,
  CI_DOT_CLASS,
  type GitAgeUnit,
  REVIEW_STATE_CLASS,
  changeRequestBadge,
  displayMomentDate,
  safeHttpUrl,
} from '../git-format';

/**
 * Các dấu nhỏ dùng chung của tab Git — APM-GIT-e: nhãn trạng thái MR, trạng thái
 * review, chấm CI, chip mã việc, link ra máy chủ git, dòng báo mốc thời gian.
 *
 * ⚠️ Không nhận asOf (luật #7, ngoại lệ 1): mọi thứ ở đây **thuần trình bày** —
 * nhận dữ liệu đã đọc sẵn qua prop, không tự `useQuery`. Mốc thời gian là việc
 * của màn lấy số.
 */

export function GitChangeRequestBadge({
  changeRequest,
}: {
  readonly changeRequest: { readonly state: GitChangeRequestState; readonly isDraft: boolean };
}): ReactNode {
  const { t } = useTranslation();
  const badge = changeRequestBadge(changeRequest);
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-[11px] font-medium',
        CHANGE_REQUEST_BADGE_CLASS[badge],
      )}
    >
      {t(`gitTab.common.state.${badge}`)}
    </span>
  );
}

export function GitReviewMark({ state }: { readonly state: GitReviewState }): ReactNode {
  const { t } = useTranslation();
  return (
    <span className={cn('whitespace-nowrap text-xs', REVIEW_STATE_CLASS[state])}>
      {t(`gitTab.common.review.${state}`)}
    </span>
  );
}

/**
 * Chấm CI của một MR. `none` (chưa có pipeline, hoặc máy chủ chưa được đọc CI)
 * ⇒ **không vẽ gì**: một chấm xám đứng cạnh các chấm xanh đọc như "CI đang tắt".
 * Màu luôn đi kèm chữ (`title` + chữ ẩn cho trình đọc màn hình).
 */
export function GitCiDot({
  status,
  url,
}: {
  readonly status: GitCiStatus;
  readonly url?: string | null | undefined;
}): ReactNode {
  const { t } = useTranslation();
  if (status === 'none') return null;
  const label = t(`gitTab.common.ci.${status}`);
  const dot = (
    <>
      <span aria-hidden className={cn('size-2 rounded-full', CI_DOT_CLASS[status])} />
      <span className="sr-only">{label}</span>
    </>
  );
  const href = safeHttpUrl(url);
  return href ? (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      title={label}
      data-testid="git-ci-dot"
      className="inline-flex size-4 shrink-0 items-center justify-center"
      onClick={(event) => event.stopPropagation()}
    >
      {dot}
    </a>
  ) : (
    <span
      title={label}
      data-testid="git-ci-dot"
      className="inline-flex size-4 shrink-0 items-center justify-center"
    >
      {dot}
    </span>
  );
}

/**
 * Chip mã việc (`GOLD-123`, `GOLD-I12`) gắn với một thứ ở git. Chỉ mang **mã**:
 * tên việc đọc ở chính màn của việc đó, theo quyền của nó (`GitLinkedTarget`).
 * Liên kết "chỉ nhắc tới" nhạt hơn liên kết "đóng việc"; gắn tay có dấu riêng.
 */
export function GitTargetChips({
  targets,
  emptyLabel,
}: {
  readonly targets: readonly GitLinkedTarget[];
  /** Chữ hiện khi không có việc nào. Bỏ trống ⇒ không render gì. */
  readonly emptyLabel?: string | undefined;
}): ReactNode {
  const { t } = useTranslation();
  if (targets.length === 0) {
    return emptyLabel ? <span className="text-xs text-muted-foreground">{emptyLabel}</span> : null;
  }
  return (
    <span className="inline-flex flex-wrap items-center gap-1">
      {targets.map((target) => (
        <span
          key={`${target.kind}:${target.id}`}
          data-testid="git-target-chip"
          title={
            target.manual
              ? t('gitTab.common.target.manual')
              : t(`gitTab.common.target.${target.intent}`)
          }
          className={cn(
            'inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 font-mono text-[11px]',
            target.intent === 'closes'
              ? 'bg-primary/10 text-primary'
              : 'bg-muted text-muted-foreground',
          )}
        >
          {target.code}
          {target.manual ? <span aria-hidden>✎</span> : null}
        </span>
      ))}
    </span>
  );
}

/** Link mở một thứ ở máy chủ git, tab mới. Địa chỉ không phải `http(s)` ⇒ chỉ là chữ. */
export function GitExternalLink({
  url,
  className,
  children,
}: {
  readonly url: string | null | undefined;
  readonly className?: string | undefined;
  readonly children: ReactNode;
}): ReactNode {
  const href = safeHttpUrl(url);
  if (!href) return <span className={className}>{children}</span>;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={cn('underline-offset-2 hover:underline', className)}
      onClick={(event) => event.stopPropagation()}
    >
      {children}
    </a>
  );
}

/** `3 ngày`, `5 giờ`, `12 phút` — từ `gitAge` / `gitDuration`. */
export function useGitAgeText(): (age: { unit: GitAgeUnit; value: number }) => string {
  const { t } = useTranslation();
  return (age) => t(`gitTab.common.age.${age.unit}`, { n: age.value });
}

/**
 * Dòng báo mốc của một phản hồi đọc (§7.3): đang xem quá khứ, và — khi mốc ấy
 * sớm hơn lúc bắt đầu có dữ liệu — "chưa có dữ liệu git trước dd/MM/yyyy", để
 * một danh sách trống không bị đọc thành "không ai làm gì".
 */
export function GitTimeNotice({ meta }: { readonly meta: GitTimeMeta | undefined }): ReactNode {
  const { t } = useTranslation();
  if (!meta || meta.asOf === null) return null;
  return (
    <p
      role="status"
      data-testid="git-time-notice"
      className="rounded-DEFAULT border border-rag-amber/40 bg-rag-amber/10 px-3 py-2 text-xs"
    >
      {t('gitTab.common.viewingAsOf', { at: displayDateTime(meta.asOf) })}
      {meta.partial && meta.historyFrom
        ? ` ${t('gitTab.common.partial', { date: displayMomentDate(meta.historyFrom) })}`
        : null}
    </p>
  );
}
